<template>
  <div
    v-loading="loading"
    element-loading-background="transparent"
    class="chart-wrapper"
  >
    <sankey-chart
      v-if="data.length"
      :data="computedData"
      :size="{ width: 1600, height: 768 }"
      :monitor-url-params="{
        flow: filter.flow,
        startAt: filter.startAt.toISOString(),
        endAt: filter.endAt.toISOString()
      }"
    ></sankey-chart>
  </div>
</template>
<style lang="scss" scoped>
.chart-wrapper {
  width: 100%;
  height: 768px;
  overflow-x: scroll;
}
</style>
<script>
import axios from "axios";
import _ from "lodash";
import SankeyChart from "./SankeyChart.vue";

export default {
  components: { SankeyChart },
  props: {
    filter: { required: true, type: Object }
  },
  data() {
    return {
      loading: false,
      data: []
    };
  },
  computed: {
    computedData() {
      return { nodes: this.nodes, links: this.links };
    },
    links() {
      const links = [];

      if (["olaRetro", "callcenterOlaRetro"].includes(this.filter.flow)) {
        links.push({
          source: "startCoupon",
          target: "raiseRetroClaimRequest",
          ...this.addProperties({
            event: "startCoupon",
            eventResult: "success"
          })
        });
      } else if (["retro", "callcenterRetro"].includes(this.filter.flow)) {
        links.push(
          {
            source: "startCoupon",
            target: "checkJuro",
            ...this.addProperties({
              event: "startCoupon",
              eventResult: "success"
            })
          },
          {
            source: "checkJuro",
            target: "juroNotFound",
            ...this.addProperties({
              event: "checkJuro",
              eventResult: "notFound"
            })
          },
          {
            source: "checkJuro",
            target: "checkFullRedemption",
            ...this.addProperties({
              event: "checkJuro",
              eventResult: "success"
            })
          },
          {
            source: "checkFullRedemption",
            target: "fullRedemption",
            ...this.addProperties({
              event: "checkFullRedemption",
              eventResult: "fullRedemption"
            })
          },
          {
            source: "checkFullRedemption",
            target: "checkFlownFlight",
            ...this.addProperties({
              event: "checkFullRedemption",
              eventResult: "success"
            })
          },
          {
            source: "checkFlownFlight",
            target: "flightNotFlown",
            ...this.addProperties({
              event: "checkFlownFlight",
              eventResult: "flightNotFlown"
            })
          },
          {
            source: "checkFlownFlight",
            target: "checkProcessedFlight",
            ...this.addProperties({
              event: "checkFlownFlight",
              eventResult: "success"
            })
          },
          {
            source: "checkProcessedFlight",
            target: "flightAlreadyProcessed",
            ...this.addProperties({
              event: "checkProcessedFlight",
              eventResult: "flightAlreadyProcessed"
            })
          },
          {
            source: "checkProcessedFlight",
            target: "checkReissuedTicket",
            ...this.addProperties({
              event: "checkProcessedFlight",
              eventResult: "success"
            })
          },
          {
            source: "checkReissuedTicket",
            target: "ticketReissued",
            ...this.addProperties({
              event: "checkReissuedTicket",
              eventResult: "ticketReissued"
            })
          },
          {
            source: "checkReissuedTicket",
            target: "checkProcessedTransaction",
            ...this.addProperties({
              event: "checkReissuedTicket",
              eventResult: "success"
            })
          },
          {
            source: "checkProcessedTransaction",
            target: "ticketProcessed",
            ...this.addProperties({
              event: "checkProcessedTransaction",
              eventResult: "ticketProcessed"
            })
          },
          {
            source: "checkProcessedTransaction",
            target: "checkML",
            ...this.addProperties({
              event: "checkProcessedTransaction",
              eventResult: "success"
            })
          },
          {
            source: "checkML",
            target: "mlInvalidRetro",
            ...this.addProperties({
              event: "checkML",
              eventResult: "invalidRetro"
            })
          },
          {
            source: "checkML",
            target: "askValidationDocs",
            ...this.addProperties({
              event: "checkML",
              eventResult: "askValidationDocs"
            })
          },
          {
            source: "checkML",
            target: "toBackend",
            ...this.addProperties({
              event: "checkML",
              eventResult: "toBackend"
            })
          },
          {
            source: "checkML",
            target: "raiseRetroClaimRequest",
            ...this.addProperties({
              event: "checkML",
              eventResult: "success"
            })
          }
        );
      }

      links.push(
        {
          source: "raiseRetroClaimRequest",
          target: "invalidMembership",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "accrual.retroclaim.Invalidmembershipnumber"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "milesFlownZero",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "accrual.common.milesFlownZeroException"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "invalidTicketName",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "accrual.process.InvalidTicketName"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "memberOutOfEnrolPeriod",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult:
              "accrual.process.ProgramMemberEnrollmentDateIsOutOfEnrollmentPeriod"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "activityDateInvalid",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "accrual.retroclaim.ActivityDateIsInvalid"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "afterMaximumPeriod",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult:
              "accrual.retroclaim.RetroclaimRequestAfterMaximumPeriod"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "retroAlreadyExist",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "accrual.retroclaim.RetroclaimRequestAlreadyExist"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "beforeMinimumPeriod",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult:
              "accrual.retroclaim.RetroclaimRequestBeforeMinimumPeriod"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "airlineCodeInvalid",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult:
              "common.master.airline.exception.AirlineCodeInvalidException"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "raiseRetroClaimError",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "error"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "rte",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "RTE",
            statusClass: "danger"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "sys005",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "SYS_005",
            statusClass: "danger"
          })
        },
        {
          source: "raiseRetroClaimRequest",
          target: "success",
          ...this.addProperties({
            event: "raiseRetroClaimRequest",
            eventResult: "success"
          })
        }
      );

      return links;
    },
    nodes() {
      let nodes = [];
      this.links.forEach(link => {
        nodes.push({ name: link.source });
        nodes.push({ name: link.target });
      });
      nodes = _.uniqBy(nodes, "name");

      return nodes;
    }
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.refreshData();
      }
    }
  },
  created() {
    this.refreshData();
  },
  methods: {
    refreshData() {
      this.loading = true;
      axios
        .get("api/v1/backend/logs", {
          params: {
            select: ["event", "event_result"],
            whereNotNull: "coupon_number",
            where: [
              `flow,=,${this.filter.flow}`,
              `created_at,>=,${this.filter.startAt.toISOString()}`,
              `created_at,<=,${this.filter.endAt.toISOString()}`
            ],
            groupBy: "event,event_result",
            count: "* as count",
            countDistinct: [
              "ffn as ffn_count",
              "ticket_number as ticket_number_count"
            ]
          }
        })
        .then(response => {
          this.data = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addProperties({ event, eventResult, statusClass }) {
      const item = _.find(this.data, {
        event,
        event_result: eventResult
      });

      return {
        value: item ? item.count : 0,
        properties: {
          event,
          eventResult,
          ffnCount: item ? item.ffn_count : 0,
          ticketNumberCount: item ? item.ticket_number_count : 0,
          statusClass
        }
      };
    }
  }
};
</script>
